// A navigation offer is not an executed scan or a body-measurement receipt.
// Its lifetime comes from the client request, never from model-owned identity.
export const MIRROR_BODY_SCAN_ROUTE = "/fitness/body-scan";

export interface MirrorBodyScanHandoff {
  actionId: string;
  ownerId: string;
  sessionId: string;
  authEpoch: number;
  sessionEpoch: number;
  request: number;
}

export interface BodyScanHandoffLifetime {
  ownerId: string | null;
  sessionId: string | null;
  authEpoch: number;
  sessionEpoch: number;
  request: number;
  mounted: boolean;
  sessionTransition: boolean;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export function bodyScanHandoffFromResults(
  results: unknown,
  origin: Omit<MirrorBodyScanHandoff, "actionId">,
): MirrorBodyScanHandoff | null {
  if (!Array.isArray(results) || !UUID.test(origin.ownerId) || !UUID.test(origin.sessionId)) return null;
  for (const result of results) {
    if (!record(result) || result.ok !== true || result.operation !== "open_vyv_body_scan" ||
      result.scope !== "body_scan" || result.evidence !== "navigation_handoff" || result.read_only !== true ||
      result.user_id !== origin.ownerId || result.session_id !== origin.sessionId ||
      result.provider_status !== "not_verified" || result.requires_user_photos !== true ||
      typeof result.action_id !== "string" || !result.action_id.trim() || result.action_id.length > 256 ||
      Array.from(result.action_id).some(character => character.charCodeAt(0) < 32) || !record(result.navigation) ||
      result.navigation.path !== MIRROR_BODY_SCAN_ROUTE || result.navigation.scan_started !== false ||
      result.navigation.scan_completed !== false) continue;
    return { ...origin, actionId: result.action_id };
  }
  return null;
}

export function isCurrentBodyScanHandoff(
  handoff: MirrorBodyScanHandoff,
  current: BodyScanHandoffLifetime,
): boolean {
  return current.mounted && !current.sessionTransition && handoff.ownerId === current.ownerId &&
    handoff.sessionId === current.sessionId && handoff.authEpoch === current.authEpoch &&
    handoff.sessionEpoch === current.sessionEpoch && handoff.request === current.request;
}

export function openBodyScanHandoff(
  handoff: MirrorBodyScanHandoff,
  current: BodyScanHandoffLifetime,
  entryEnabled: boolean,
  navigate: (path: string) => void,
): boolean {
  if (!entryEnabled || !isCurrentBodyScanHandoff(handoff, current)) return false;
  navigate(MIRROR_BODY_SCAN_ROUTE);
  return true;
}
